import { FC, useEffect } from 'react';
import { Box, Divider, Link, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { TextInput } from '../../components/forms/text-input.tsx';
import { PasswordInput } from '../../components/forms/password-input.tsx';
import { SubmitButton } from '../../components/forms/submit.button.tsx';
import { useGenerateAuthenticationOptionsQuery, useLoginMutation, useVerifyAuthenticationResponseMutation } from '../../graphql/generated/operations/auth.ts';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../../constants/paths.ts';
import { useSnackbar } from '../../contexts/snackbar.context.tsx';
import { useAuth } from '../../contexts/auth.context.tsx';
import { startAuthentication } from '@simplewebauthn/browser';
import { trackEvent } from '../../utils/analytics.ts';

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(8, 'Password should be at least 8 characters').required('Password is required')
});

const initialValues = {
  email: '',
  password: ''
};

export const LoginPage: FC = () => {
  const [login, { data, error }] = useLoginMutation();
  const { setTokens, userId, isLoggedIn } = useAuth();
  const [loginWithPasskey, { data: passKeyCreds }] = useVerifyAuthenticationResponseMutation();
  const { data: authOptions } = useGenerateAuthenticationOptionsQuery({
    variables: {
      userId: userId || ''
    },
    skip: !userId
  });
  const navigate = useNavigate();
  const { push } = useSnackbar();

  useEffect(() => {
    if (isLoggedIn) {
      navigate(Paths.HOME);
    }
  }, [isLoggedIn]);

  const handleLogin = async (values: typeof initialValues) => {
    trackEvent('login_attempt', { method: 'password' });
    return login({
      variables: {
        input: {
          email: values.email,
          password: values.password
        }
      }
    });
  };

  useEffect(() => {
    if (authOptions?.generateAuthenticationOptions && userId) {
      const signInWithPassKey = async () => {
        const authResp = await startAuthentication(authOptions.generateAuthenticationOptions, true);
        loginWithPasskey({ variables: { authResp, userId } });
      };
      signInWithPassKey();
    }
  }, [authOptions, userId]);

  useEffect(() => {
    if (passKeyCreds) {
      trackEvent('login_success', { method: 'passkey' });
      setTokens(passKeyCreds.verifyAuthenticationResponse.accessToken, passKeyCreds.verifyAuthenticationResponse.refreshToken);
      navigate(Paths.HOME);
    }
  }, [passKeyCreds]);

  useEffect(() => {
    if (data?.login) {
      trackEvent('login_success', { method: 'password' });
      setTokens(data.login.accessToken, data.login.refreshToken);
      navigate(Paths.HOME);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      trackEvent('login_error');
      push({ message: error.message, type: 'error' });
    }
  }, [error]);

  return (
    <Stack gap={3}>
      <Typography variant="h1" component="h1">
        Login
      </Typography>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleLogin}>
        <Form autoComplete="on">
          <Stack gap={2}>
            <TextInput name="email" label="Email" autoComplete="email webauthn" />
            <PasswordInput name="password" label="Password" autoComplete="current-password webauthn" />
            <SubmitButton>Login</SubmitButton>
          </Stack>
        </Form>
      </Formik>
      <Divider />
      <Stack gap={1} direction="row">
        <Box flexGrow={1} />
        <Typography variant="body1" component="p" sx={{ textAlign: 'end' }}>
          Don't have an account? <Link onClick={() => navigate(Paths.REGISTER)}>Register</Link>
        </Typography>
      </Stack>
    </Stack>
  );
};
